import React, { useState, useEffect } from "react";
import { getEtapes } from "../services/api";
import ModaleV2 from "./ModaleV2";
import { dateCourte } from "../services/projet";

// ===================================================================
// The Crowd v2 — Fiche projet : modales des jalons
//
// Sorties de ProjetJalonsV2.js (taille du fichier) :
// - ModaleJalon : creation et modification d'un jalon (chef, co-chefs),
//   avec cible chiffree, horizon et etapes liees ;
// - ModaleSimple : confirmation ou champ unique (proposer, confirmer,
//   ne pas confirmer avec un mot, valeur actuelle, suppression).
// ===================================================================

function nombreOuNull(v) {
  return v === "" || v === null || v === undefined ? null : Number(v);
}

export function afficherNombre(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : String(n).replace(".", ",");
}

// --- Creation / modification (chef, co-chefs) ---

export function ModaleJalon({ jalon, projetId, collaborateurId, onEnregistrer, onClose }) {
  const [valeurs, setValeurs] = useState(() => ({
    intitule: jalon?.intitule || "",
    critere: jalon?.critere || "",
    cible_visee: jalon?.cible_visee ?? "",
    cible_actuelle: jalon?.cible_actuelle ?? "",
    cible_unite: jalon?.cible_unite || "",
    horizon: dateCourte(jalon?.horizon),
  }));
  const [etapeIds, setEtapeIds] = useState(() => (jalon?.etapes || []).map((e) => e.id));
  const [etapes, setEtapes] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    getEtapes(projetId, collaborateurId)
      .then((d) => setEtapes(d.etapes || []))
      .catch(() => setEtapes([]));
  }, [projetId, collaborateurId]);

  const changer = (champ) => (e) => setValeurs((v) => ({ ...v, [champ]: e.target.value }));
  const basculer = (id) =>
    setEtapeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const enregistrer = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await onEnregistrer({
        intitule: valeurs.intitule.trim(),
        critere: valeurs.critere.trim(),
        cible_visee: nombreOuNull(valeurs.cible_visee),
        cible_actuelle: nombreOuNull(valeurs.cible_actuelle),
        cible_unite: valeurs.cible_unite.trim() || null,
        horizon: valeurs.horizon || null,
        etape_ids: etapeIds,
      });
    } catch (err) {
      setErreur(err.message);
      setEnCours(false);
    }
  };

  return (
    <ModaleV2
      titre={jalon ? "Modifier le jalon" : "Nouveau jalon"}
      onClose={onClose}
      bloquee={enCours}
      actions={
        <>
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={onClose} disabled={enCours}>Annuler</button>
          <button type="button" className="v2-btn v2-btn-principal" onClick={enregistrer}
            disabled={enCours || !valeurs.intitule.trim() || !valeurs.critere.trim()}>
            {enCours ? "Enregistrement…" : "Enregistrer"}
          </button>
        </>
      }
    >
      {erreur && <div className="v2-msg v2-msg-erreur">{erreur}</div>}
      <div className="v2-champ">
        <label className="v2-label" htmlFor="jalon-intitule">Ce qu'on vise<span className="v2-requis">*</span></label>
        <input id="jalon-intitule" value={valeurs.intitule} onChange={changer("intitule")} />
      </div>
      <div className="v2-champ">
        <label className="v2-label" htmlFor="jalon-critere">Critère de réussite<span className="v2-requis">*</span></label>
        <textarea id="jalon-critere" rows={2} value={valeurs.critere} onChange={changer("critere")}
          placeholder="Ce qui permet de dire « atteint », en une phrase" />
      </div>
      <fieldset className="v2-groupe-champs">
        <legend className="v2-label">Cible chiffrée (optionnelle)</legend>
        <div className="v2-champs-ligne">
          <div className="v2-champ">
            <label className="v2-label" htmlFor="jalon-actuelle">Valeur actuelle</label>
            <input id="jalon-actuelle" type="number" value={valeurs.cible_actuelle} onChange={changer("cible_actuelle")} />
          </div>
          <div className="v2-champ">
            <label className="v2-label" htmlFor="jalon-visee">Valeur visée</label>
            <input id="jalon-visee" type="number" value={valeurs.cible_visee} onChange={changer("cible_visee")} />
          </div>
        </div>
        <div className="v2-champ">
          <label className="v2-label" htmlFor="jalon-unite">Unité</label>
          <input id="jalon-unite" value={valeurs.cible_unite} onChange={changer("cible_unite")} placeholder="% des postes, tickets, jours…" />
        </div>
      </fieldset>
      <div className="v2-champ v2-champ-court">
        <label className="v2-label" htmlFor="jalon-horizon">Horizon</label>
        <input id="jalon-horizon" type="date" value={valeurs.horizon} onChange={changer("horizon")} />
      </div>
      {etapes.length > 0 && (
        <fieldset className="v2-groupe-champs">
          <legend className="v2-label">Étapes liées (indicatif)</legend>
          <div className="v2-cases">
            {etapes.map((e) => (
              <label key={e.id} className="v2-case-simple">
                <input type="checkbox" checked={etapeIds.includes(e.id)} onChange={() => basculer(e.id)} />
                <span>{e.nom}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
    </ModaleV2>
  );
}

// --- Petite modale a un champ (valeur actuelle, mot du chef, confirmation) ---

export function ModaleSimple({ titre, libelleAction, danger, champ, aide, onValider, onClose }) {
  const [valeur, setValeur] = useState(champ?.initial ?? "");
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  const valider = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await onValider(valeur);
    } catch (err) {
      setErreur(err.message);
      setEnCours(false);
    }
  };

  const invalide = champ?.requis && !String(valeur).trim();

  return (
    <ModaleV2
      titre={titre}
      onClose={onClose}
      bloquee={enCours}
      actions={
        <>
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={onClose} disabled={enCours}>Annuler</button>
          <button type="button" className={`v2-btn ${danger ? "v2-btn-secondaire v2-btn-danger-texte" : "v2-btn-principal"}`}
            onClick={valider} disabled={enCours || invalide}>
            {enCours ? "…" : libelleAction}
          </button>
        </>
      }
    >
      {erreur && <div className="v2-msg v2-msg-erreur">{erreur}</div>}
      {aide && <p className="v2-champ-aide">{aide}</p>}
      {champ && (
        <div className="v2-champ">
          <label className="v2-label" htmlFor="jalon-champ">{champ.libelle}</label>
          {champ.type === "textarea" ? (
            <textarea id="jalon-champ" rows={3} value={valeur} onChange={(e) => setValeur(e.target.value)} />
          ) : (
            <input id="jalon-champ" type={champ.type || "text"} value={valeur} onChange={(e) => setValeur(e.target.value)} />
          )}
        </div>
      )}
    </ModaleV2>
  );
}
